import { useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { emitMessageGroupToast } from "../../notifications/utils/messageGroupToastBus";
import { getMessageText } from "../../notifications/utils/showInternalMessageNotification";
import { useSocket } from "../../context/SocketContext";
import { useChatDock } from "../chat-dock";
import { useMessageGroupsListQuery } from "./hooks/useMessageGroupsListQuery";
import { useMessageGroupsListSocket } from "./hooks/useMessageGroupSocket";

export const MessageGroupsSocketBridge = () => {
  const { socket } = useSocket();
  const { openChats, focusedKey } = useChatDock();
  const { uid, workforces, companies, company, activeCompanyId } = useSelector((state: any) => state.userState);

  const companyNames = useMemo(() => {
    const names = new Map<string, string>();

    (companies ?? []).forEach((membership: any) => {
      if (membership?.companyId == null) return;
      const name = membership.company?.businessName || membership.company?.name;
      if (name) names.set(String(membership.companyId), name);
    });

    return names;
  }, [companies]);

  const focusedGroupUid = useMemo(() => {
    const focused = openChats.find((chat) => chat.key === focusedKey && !chat.minimized);
    return focused?.target.type === "group" ? focused.target.groupUid : null;
  }, [openChats, focusedKey]);

  const { groups } = useMessageGroupsListQuery();
  useMessageGroupsListSocket(socket, activeCompanyId, focusedGroupUid);

  const groupsRef = useRef(groups);
  groupsRef.current = groups;

  const identityRef = useRef({ uid, workforceUids: [] as string[] });
  identityRef.current = { uid, workforceUids: (workforces || []).map((workforce: any) => workforce.uid).filter(Boolean) };

  const openGroupRef = useRef(focusedGroupUid);
  openGroupRef.current = focusedGroupUid;

  const companyNamesRef = useRef(companyNames);
  companyNamesRef.current = companyNames;

  const processedUidsRef = useRef(new Set<string>());

  useEffect(() => {
    if (!socket) return;

    const handleMessage = ({ groupUid, message, groupName: eventGroupName, channel: eventChannel }: any) => {
      if (!groupUid || !message || message.kind === "system") return;

      const messageCompanyId = message.companyId ?? null;
      const isFromActiveCompany =
        messageCompanyId == null || activeCompanyId == null || Number(messageCompanyId) === Number(activeCompanyId);

      if (document.visibilityState === "visible" && isFromActiveCompany && groupUid === openGroupRef.current) return;

      const messageUid = message.uid;
      if (!messageUid || processedUidsRef.current.has(messageUid)) return;
      processedUidsRef.current.add(messageUid);
      if (processedUidsRef.current.size > 100) {
        const first = processedUidsRef.current.values().next().value;
        if (first) processedUidsRef.current.delete(first);
      }

      const { uid: myUid, workforceUids } = identityRef.current;
      const sender = message.sender ?? {};
      const isFromMe =
        (sender.type === "user" && sender.uid === myUid) || (sender.type === "workforce" && workforceUids.includes(sender.uid));
      if (isFromMe) return;

      const group = isFromActiveCompany ? groupsRef.current.find((item) => item.groupUid === groupUid) : null;
      const groupName = eventGroupName ?? group?.name ?? message.groupName ?? "Group";
      const channel = eventChannel ?? group?.channel ?? (message.content?.channel === "sms" ? "sms" : "internal");
      const mentions = Array.isArray(message.mentions) ? message.mentions : [];
      const mentionsMe = mentions.some(
        (mention: any) =>
          (mention.type === "user" && mention.uid === myUid) || (mention.type === "workforce" && workforceUids.includes(mention.uid)),
      );

      const text = getMessageText(message);
      const senderName = sender.name?.trim() || "Someone";

      emitMessageGroupToast({
        uid: messageUid,
        groupUid,
        groupName,
        senderName,
        companyId: messageCompanyId ?? activeCompanyId ?? null,
        companyName:
          companyNamesRef.current.get(String(messageCompanyId ?? activeCompanyId)) ??
          (isFromActiveCompany ? company?.businessName : null) ??
          null,
        text: text || "",
        mentionsMe,
        channel,
      });
    };

    socket.on("messageGroupMessage", handleMessage);
    return () => {
      socket.off("messageGroupMessage", handleMessage);
    };
  }, [socket, activeCompanyId, company?.businessName]);

  return null;
};
